import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { IconManage } from '../../../../../shared/Icons';
import BrandProposalModal from '../../manage/calendar/components/BrandProposalModal';
import { CalendarListView } from '../../manage/calendar/components/CalendarListView';
import { CalendarUpdateModal } from '../../manage/calendar/components/modal/CalendarUpdateModal';
import { ManageService } from '../../manage/service';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { iManageFilters, iManageList } from '../../manage/calendar/dto/IManageCalendar';
import InfiniteScroll from 'react-infinite-scroll-component';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import { CalendarListLoaderSkeleton } from '../../manage/calendar/components/CalendarListViewSkeleton';

export function CampaignManageList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();
    const [filterParams, setFilterParams] = useState<iManageFilters>({
        page: 1,
        perPage: 10,
        totalPages: 0,
        sortType: 'DESC',
    });
    const [isProposalOpened, setIsProposalOpened] = useState(false);
    const [triggerUpdate, setTriggerUpdate] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState();
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(0);
    const [allManages, setAllManages] = useState<iManageList[]>();
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();
    const linkToManage = () => {
        navigate(`${InternalRoutes.BRAND_CAMPAIGN_MANAGE}/${id}`);
    };

    useEffect(() => {
        if (!id) {
            return;
        }

        const queryKey = `${id}-${JSON.stringify({ ...filterParams, perPage: undefined })}`;
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsLoading(true);
        }

        let isCurrent = true;
        ManageService.getManages(+id, filterParams)
            .then((response: any) => {
                if (!isCurrent) return;
                setAllManages(response?.data);
                setTotal(response?.count);
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [JSON.stringify(filterParams), id, triggerUpdate]);

    const approvePosting = () => {
        ManageService.approveEvent(selectedEventId)
            .then(response => {
                setSelectedEvent(response);
                // setFilterParams(prev => ({...prev, update: prev.page + 1})); // just update list and trigger api call
                setTriggerUpdate(prevState => !prevState);
                SuccessToast(t('general.toasts.successfullyApproved'));
            })
            .catch(error => ErrorToast(error));
    };

    useEffect(() => {
        if (!!selectedEventId) {
            setSelectedEvent(undefined);
            ManageService.getEventById(selectedEventId)
                .then(response => {
                    setSelectedEvent(response);
                })
                .catch(error => ErrorToast(error));
        }
    }, [selectedEventId]);
    const loadMoreData = () => {
        setFilterParams(prev => ({ ...prev, perPage: prev.perPage + 10 }));
    };

    return (
        <>
            <BrandProposalModal
                setState={setSelectedEvent as unknown as React.Dispatch<SetStateAction<iManageList>>}
                state={selectedEvent as unknown as iManageList}
                isModalOpened={isProposalOpened}
                setTriggerUpdate={setTriggerUpdate}
                handleCloseModal={() => {
                    setIsProposalOpened(false);
                    setSelectedEventId(0);
                }}
            />
            <CalendarUpdateModal
                show={showUpdateModal}
                setState={setSelectedEvent as unknown as React.Dispatch<SetStateAction<iManageList>>}
                closeModal={() => setShowUpdateModal(false)}
                setSelectedEventId={setSelectedEventId}
                onSend={approvePosting}
                state={selectedEvent as unknown as iManageList}
            />
            <CardLayout>
                <div className="d-flex justify-content-between cursor-pointer">
                    <h6 className="textHover" onClick={linkToManage}>
                        <IconManage width="20" height="20" color={MUTED_TEXT_COLOR} classes={' mr-2'} />
                        {t('brand.campaign.dashboard.manage.manage')}
                    </h6>
                </div>
                <p className="text-muted my-2">{t('brand.campaign.dashboard.manage.short_overview')}</p>
                <div id="scrollableDiv" style={{ maxHeight: '520px', overflow: 'auto' }}>
                    {isLoading ? (
                        <CalendarListView
                            listOfInfluencers={[]}
                            filterParams={filterParams}
                            setSelectedEventId={setSelectedEventId}
                            setIsProposalOpened={setIsProposalOpened}
                            setShowUpdateModal={setShowUpdateModal}
                            setFilterParams={setFilterParams}
                            isLoading
                        />
                    ) : !!allManages?.length ? (
                        <InfiniteScroll
                            dataLength={allManages?.length}
                            next={loadMoreData}
                            endMessage={
                                <p
                                    className={`text-center ${
                                        allManages?.length !== total || filterParams?.perPage <= 10 ? 'd-none' : ''
                                    }`}
                                >
                                    <b>You have seen all!</b>
                                </p>
                            }
                            hasMore={allManages?.length < total}
                            scrollableTarget="scrollableDiv"
                            loader={<CalendarListLoaderSkeleton />}
                        >
                            <CalendarListView
                                listOfInfluencers={allManages}
                                filterParams={filterParams}
                                setSelectedEventId={setSelectedEventId}
                                setIsProposalOpened={setIsProposalOpened}
                                setShowUpdateModal={setShowUpdateModal}
                                setFilterParams={setFilterParams}
                            />
                        </InfiniteScroll>
                    ) : (
                        <EmptyState
                            btnText={t('brand.campaign.dashboard.manage.manage')}
                            iconPath="/icons/clock.png"
                            navigateTo={`/brand/campaign/manage/${id}`}
                            desc={t('brand.campaign.dashboard.manage.empty_manage')}
                        />
                    )}
                </div>
            </CardLayout>
        </>
    );
}
